import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
    AuthorizationGuard,
    ClaimGuard,
    FeatureFlagGuard,
    WorkflowMonitorGuard,
    Routes
} from '@lsq-frontend/common-ui';
import { adminAtlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { UnImpersonateGuard } from '../guards/un-impersonate.guard';
import { AdminBuyersComponent } from './buyers/admin-buyers.component';
import { AdminCreateBuyerComponent } from './buyers/create-buyer/create-buyer.component';
import { BuyerCompanyInformationComponent } from './buyers/manage-buyer/company-information/buyer-company-information.component';
import { AdminManageBuyerCompanyLocationComponent } from './buyers/manage-buyer/company-location/buyer-company-location.component';
import { AdminManageBuyerConfigurationComponent } from './buyers/manage-buyer/configuration/buyer-configuration.component';
import { AdminManageBuyerContactComponent } from './buyers/manage-buyer/contact/buyer-contact.component';
import { ManageBuyerComponent } from './buyers/manage-buyer/manage-buyer.component';
import { AdminReviewBuyerComponent } from './buyers/manage-buyer/review-buyer/review-buyer.component';
import { ClientManagementComponent } from './client-management/client-management.component';
import { AdminProspectsComponent } from './prospects/admin-prospects.component';
import { ManageProspectComponent } from './prospects/manage-prospect/manage-prospect.component';
import { ManageProspectActivityComponent } from './prospects/manage-prospect/prospect-activity/prospect-activity.component';
import { ManageProspectDetailsComponent } from './prospects/manage-prospect/prospect-details/prospect-details.component';
import { ManageProspectMigrationComponent } from './prospects/manage-prospect/prospect-migration/prospect-migration.component';
import { ManageProspectUsersComponent } from './prospects/manage-prospect/prospect-users/prospect-users.component';
import { AdminSuppliersComponent } from './suppliers/admin-suppliers.component';
import { CreateSuppliersComponent } from './suppliers/create-suppliers/create-suppliers.component';
import { ManageSupplierImplementationComponent } from './suppliers/manage-supplier/implementation/manage-supplier-implementation.component';
import { ManageSupplierInformationComponent } from './suppliers/manage-supplier/information/manage-supplier-information.component';
import { ManageSupplierComponent } from './suppliers/manage-supplier/manage-supplier.component';
import { ManageSupplierPaymentMethodsComponent } from './suppliers/manage-supplier/payment-methods/manage-supplier-payment-methods.component';
import { AdminUserManagementComponent } from './user-management/user-management.component';
import { UiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { AdminInvoicesComponent } from './invoices/admin-invoices.component';
import { AdminDocumentsComponent } from './documents/documents.component';
import { DynamicAudit } from '@lsqlabs/dr-application-shared';
import { AdminPurchaseRequestsComponent } from './purchase-requests/purchase-requests.component';

export const adminRoutes: Routes = [
    {
        path: UiRoutes.Home,
        component: ClientManagementComponent,
        canActivate: [ClaimGuard, UnImpersonateGuard],
        data: {
            title: adminAtlas.ADMIN_OVERVIEW.title,
            claims: adminAtlas.ADMIN_OVERVIEW.claims
        }
    },
    {
        path: adminAtlas.ADMIN_USER_MANAGEMENT.path,
        component: AdminUserManagementComponent,
        canActivate: [ClaimGuard, UnImpersonateGuard, FeatureFlagGuard],
        data: {
            title: adminAtlas.ADMIN_USER_MANAGEMENT.title,
            claims: adminAtlas.ADMIN_USER_MANAGEMENT.claims,
            featureFlags: adminAtlas.ADMIN_USER_MANAGEMENT.featureFlags
        }
    },
    {
        path: adminAtlas.ADMIN_DOCUMENTS.path,
        component: AdminDocumentsComponent,
        canActivate: [ClaimGuard, UnImpersonateGuard, FeatureFlagGuard, AuthorizationGuard],
        data: {
            title: adminAtlas.ADMIN_DOCUMENTS.title,
            claims: adminAtlas.ADMIN_DOCUMENTS.claims,
            featureFlags: adminAtlas.ADMIN_DOCUMENTS.featureFlags
        },
        children: [
            {
                path: adminAtlas.ADMIN_DOCUMENTS.children.ADMIN_DOCUMENTS_AUDIT.path,
                component: DynamicAudit,
                data: {
                    title: adminAtlas.ADMIN_DOCUMENTS.children.ADMIN_DOCUMENTS_AUDIT.title
                }
            }
        ]
    },
    {
        path: adminAtlas.ADMIN_SUPPLIERS.path,
        component: AdminSuppliersComponent,
        canActivate: [ClaimGuard, UnImpersonateGuard],
        canDeactivate: [WorkflowMonitorGuard],
        data: {
            title: adminAtlas.ADMIN_SUPPLIERS.title,
            claims: adminAtlas.ADMIN_SUPPLIERS.claims
        }
    },
    {
        path: adminAtlas.ADMIN_MANAGE_SUPPLIER.path,
        component: ManageSupplierComponent,
        canActivate: [ClaimGuard],
        data: {
            title: adminAtlas.ADMIN_MANAGE_SUPPLIER.title,
            claims: adminAtlas.ADMIN_MANAGE_SUPPLIER.claims
        },
        children: [
            {
                path:
                    adminAtlas.ADMIN_MANAGE_SUPPLIER.children.ADMIN_MANAGE_SUPPLIER_INFORMATION
                        .path,
                component: ManageSupplierInformationComponent,
                canDeactivate: [WorkflowMonitorGuard],
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_SUPPLIER.children.ADMIN_MANAGE_SUPPLIER_INFORMATION
                            .title
                }
            },
            {
                path:
                    adminAtlas.ADMIN_MANAGE_SUPPLIER.children.ADMIN_MANAGE_SUPPLIER_IMPLEMENTATION
                        .path,
                component: ManageSupplierImplementationComponent,
                canDeactivate: [WorkflowMonitorGuard],
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_SUPPLIER.children
                            .ADMIN_MANAGE_SUPPLIER_IMPLEMENTATION.title
                }
            },
            {
                path:
                    adminAtlas.ADMIN_MANAGE_SUPPLIER.children.ADMIN_MANAGE_SUPPLIER_PAYMENT_METHODS
                        .path,
                component: ManageSupplierPaymentMethodsComponent,
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_SUPPLIER.children
                            .ADMIN_MANAGE_SUPPLIER_PAYMENT_METHODS.title
                }
            }
        ]
    },
    {
        path: adminAtlas.ADMIN_CREATE_SUPPLIER.path,
        component: CreateSuppliersComponent,
        canActivate: [ClaimGuard],
        data: {
            title: adminAtlas.ADMIN_CREATE_SUPPLIER.title,
            claims: adminAtlas.ADMIN_CREATE_SUPPLIER.claims
        }
    },
    {
        path: adminAtlas.ADMIN_BUYERS.path,
        component: AdminBuyersComponent,
        canActivate: [ClaimGuard, UnImpersonateGuard],
        data: {
            title: adminAtlas.ADMIN_BUYERS.title,
            claims: adminAtlas.ADMIN_BUYERS.claims
        }
    },
    {
        path: adminAtlas.ADMIN_BUYER.path,
        children: [
            {
                path: adminAtlas.ADMIN_BUYER.children.ADMIN_CREATE_BUYER.path,
                component: AdminCreateBuyerComponent,
                canActivate: [ClaimGuard, FeatureFlagGuard],
                data: {
                    title: adminAtlas.ADMIN_BUYER.children.ADMIN_CREATE_BUYER.title,
                    claims: adminAtlas.ADMIN_BUYER.children.ADMIN_CREATE_BUYER.claims,
                    featureFlags: adminAtlas.ADMIN_BUYER.children.ADMIN_CREATE_BUYER.featureFlags
                }
            },
            {
                path: adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.path,
                component: ManageBuyerComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.title,
                    claims: adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.claims
                },
                children: [
                    {
                        path: UiRoutes.Home,
                        redirectTo:
                            adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                .ADMIN_MANAGE_BUYER_COMPANY.path
                    },
                    {
                        path:
                            adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                .ADMIN_MANAGE_BUYER_COMPANY.path,
                        component: BuyerCompanyInformationComponent,
                        canDeactivate: [WorkflowMonitorGuard],
                        data: {
                            title:
                                adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                    .ADMIN_MANAGE_BUYER_COMPANY.title
                        }
                    },
                    {
                        path:
                            adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                .ADMIN_MANAGE_BUYER_LOCATION.path,
                        component: AdminManageBuyerCompanyLocationComponent,
                        canDeactivate: [WorkflowMonitorGuard],
                        data: {
                            title:
                                adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                    .ADMIN_MANAGE_BUYER_LOCATION.title
                        }
                    },
                    {
                        path:
                            adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                .ADMIN_MANAGE_BUYER_CONFIGURATION.path,
                        component: AdminManageBuyerConfigurationComponent,
                        canDeactivate: [WorkflowMonitorGuard],
                        data: {
                            title:
                                adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                    .ADMIN_MANAGE_BUYER_CONFIGURATION.title
                        }
                    },
                    {
                        path:
                            adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                .ADMIN_REVIEW_BUYER.path,
                        component: AdminReviewBuyerComponent,
                        canDeactivate: [WorkflowMonitorGuard],
                        data: {
                            title:
                                adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                    .ADMIN_REVIEW_BUYER.title
                        }
                    },
                    {
                        path:
                            adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                .ADMIN_MANAGE_BUYER_CONTACT.path,
                        component: AdminManageBuyerContactComponent,
                        canDeactivate: [WorkflowMonitorGuard],
                        data: {
                            title:
                                adminAtlas.ADMIN_BUYER.children.ADMIN_MANAGE_BUYER.children
                                    .ADMIN_MANAGE_BUYER_CONTACT.title
                        }
                    }
                ]
            }
        ]
    },
    {
        path: adminAtlas.ADMIN_PROSPECTS.path,
        component: AdminProspectsComponent,
        canActivate: [ClaimGuard],
        data: {
            title: adminAtlas.ADMIN_PROSPECTS.title,
            claims: adminAtlas.ADMIN_PROSPECTS.claims
        }
    },
    {
        path: adminAtlas.ADMIN_INVOICES.path,
        component: AdminInvoicesComponent,
        canActivate: [ClaimGuard],
        data: {
            title: adminAtlas.ADMIN_INVOICES.title,
            claims: adminAtlas.ADMIN_INVOICES.claims
        }
    },
    {
        path: adminAtlas.ADMIN_PURCHASE_REQUESTS.path,
        component: AdminPurchaseRequestsComponent,
        canActivate: [ClaimGuard],
        data: {
            title: adminAtlas.ADMIN_PURCHASE_REQUESTS.title,
            claims: adminAtlas.ADMIN_PURCHASE_REQUESTS.claims
        }
    },
    {
        path: adminAtlas.ADMIN_MANAGE_PROSPECT.path,
        component: ManageProspectComponent,
        canActivate: [ClaimGuard],
        data: {
            title: adminAtlas.ADMIN_MANAGE_PROSPECT.title,
            claims: adminAtlas.ADMIN_MANAGE_PROSPECT.claims
        },
        children: [
            {
                path: '',
                redirectTo:
                    adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_DETAILS.path
            },
            {
                path: adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_DETAILS.path,
                component: ManageProspectDetailsComponent,
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_DETAILS
                            .title
                }
            },
            {
                path: adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_ACTIVITY.path,
                component: ManageProspectActivityComponent,
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_ACTIVITY
                            .title
                }
            },
            {
                path:
                    adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_MIGRATION.path,
                component: ManageProspectMigrationComponent,
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_MIGRATION
                            .title
                }
            },
            {
                path: adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_AUDIT.path,
                component: DynamicAudit,
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_AUDIT.title
                }
            },
            {
                path: adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_USERS.path,
                component: ManageProspectUsersComponent,
                data: {
                    title:
                        adminAtlas.ADMIN_MANAGE_PROSPECT.children.ADMIN_MANAGE_PROSPECT_USERS.title
                }
            }
        ]
    }
];

@NgModule({
    imports: [RouterModule.forChild(adminRoutes)],
    exports: [RouterModule]
})
export class AdminRoutingModule {}
